import { BroadcastChannel, type BroadcastChannelOptions } from 'broadcast-channel';
import { useCallback, useEffect, useRef } from 'react';

export type UseBroadcastChannelOptions = Omit<BroadcastChannelOptions, 'node'>;

/**
 * BroadcastChannel 기반으로 메시지를 송수신하는 훅.
 * 동일 출처의 다른 창/웹뷰와 통신할 때 사용합니다.
 */
export const useBroadcastChannel = <Message>(
  channelName: string,
  onMessage?: (message: Message) => void,
  options?: UseBroadcastChannelOptions,
) => {
  const channelRef = useRef<BroadcastChannel<Message> | null>(null);
  const onMessageRef = useRef(onMessage);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const postMessage = useCallback((message: Message) => {
    channelRef.current?.postMessage(message);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return () => undefined;
    }

    const channel = new BroadcastChannel<Message>(channelName, options);
    channelRef.current = channel;

    channel.onmessage = (message) => {
      onMessageRef.current?.(message);
    };

    return () => {
      channelRef.current = null;
      channel.close();
    };
  }, [channelName, options]);

  return { postMessage } as const;
};
